// AGC: CreateWithGPS - GPS path recording for lot definition
// AGC: Map centers on farm or user location; real GPS when recording on native
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { MapContainer, TileLayer, Polyline, Marker, useMap } from 'react-leaflet'
import { ArrowLeft, Play, Pause, StopCircle, Navigation, Locate } from 'lucide-react'
import { calculatePolygonArea, calculatePolygonPerimeter, formatArea, formatPerimeter } from '../../services/polygonService'
import { useFarms } from '../../context/FarmContext'
import { PermissionBlockedModal } from '../common/PermissionBlockedModal'
import { PermissionBlockedError } from '../../services/nativeGeolocation'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

// AGC: Fix Leaflet default marker icon issue
import icon from 'leaflet/dist/images/marker-icon.png'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
})

L.Marker.prototype.options.icon = DefaultIcon

interface CreateWithGPSProps {
  lotName: string
  onCancel: () => void
  onSave: (data: { name: string; coordinates: [number, number][]; area: number; perimeter: number }) => void
}

/**
 * AGC: Map auto-center component
 */
interface AutoCenterProps {
  center: [number, number]
}

const AutoCenter: React.FC<AutoCenterProps> = ({ center }) => {
  const map = useMap()
  useEffect(() => {
    map.setView(center, map.getZoom())
  }, [center, map])
  return null
}

// AGC: Expose map ref for external "My Location" button
const MapController: React.FC<{ onMapReady: (map: L.Map) => void }> = ({ onMapReady }) => {
  const map = useMap()
  useEffect(() => {
    onMapReady(map)
  }, [map, onMapReady])
  return null
}

/**
 * AGC: CreateWithGPS - Simulates GPS path walking for lot definition
 */
export const CreateWithGPS: React.FC<CreateWithGPSProps> = ({ lotName, onCancel, onSave }) => {
  const { currentFarm } = useFarms()
  
  // AGC: Starting position - use FARM GPS coordinates, fallback to default
  const farmCenter: [number, number] = useMemo(() => {
    if (currentFarm?.coordinates && currentFarm.coordinates.length === 2) {
      console.log(`📍 AGC: GPS starting position from farm "${currentFarm.name}"`)
      return currentFarm.coordinates
    }
    // AGC: Fallback to default location (Brasília region)
    return [-15.7975, -47.8919]
  }, [currentFarm])
  
  const [currentPosition, setCurrentPosition] = useState<[number, number]>(farmCenter)
  const [pathPoints, setPathPoints] = useState<[number, number][]>([])
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [calculatedData, setCalculatedData] = useState<{ area: number; perimeter: number } | null>(null)
  const [isGettingLocation, setIsGettingLocation] = useState(false)
  const [showPermBlocked, setShowPermBlocked] = useState(false)
  const mapRef = useRef<L.Map | null>(null)

  // AGC: My Location - center map on device GPS
  // AGC: Delegates permission handling to nativeGeolocation / permissionHelper.
  // AGC: No pre-request on mount -- try-first approach handles all permission states.
  const handleGoToMyLocation = useCallback(async () => {
    if (isRecording && !isPaused) return
    setIsGettingLocation(true)
    try {
      const { getCurrentPosition } = await import('../../services/nativeGeolocation')
      const coords = await getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 })
      const pos: [number, number] = [coords.latitude, coords.longitude]
      setCurrentPosition(pos)
      if (mapRef.current) {
        mapRef.current.setView(pos, 18)
      }
      if (pathPoints.length === 0) {
        setPathPoints([pos])
      }
    } catch (err: any) {
      console.error('AGC: Go to my location failed:', err)
      if (err instanceof PermissionBlockedError) {
        // AGC: Permission permanently denied -- show modal with Settings deep-link
        setShowPermBlocked(true)
      } else {
        alert(err?.message || 'Não foi possível obter sua localização.')
      }
    } finally {
      setIsGettingLocation(false)
    }
  }, [isRecording, isPaused, pathPoints.length])

  // AGC: Auto-center on device GPS on mount (not farm center)
  useEffect(() => {
    if (pathPoints.length > 0) return
    let cancelled = false
    const centerOnDevice = async () => {
      try {
        const { getCurrentPosition } = await import('../../services/nativeGeolocation')
        const coords = await getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 })
        if (!cancelled) {
          const pos: [number, number] = [coords.latitude, coords.longitude]
          setCurrentPosition(pos)
          if (mapRef.current) {
            mapRef.current.setView(pos, 18)
          }
        }
      } catch {
        // AGC: Fallback to farm center if GPS fails
        if (!cancelled) {
          setCurrentPosition(farmCenter)
        }
      }
    }
    centerOnDevice()
    return () => { cancelled = true }
  }, [farmCenter, pathPoints.length])

  // AGC: GPS position buffer for moving average filter (smooths noisy signal)
  const posBufferRef = useRef<[number, number][]>([])
  const GPS_BUFFER_SIZE = 5

  // AGC: Real GPS tracking during recording using watchPosition
  useEffect(() => {
    if (!isRecording || isPaused) return

    let watchId: string | number | null = null
    let cancelled = false

    const startWatch = async () => {
      try {
        const { watchPosition } = await import('../../services/nativeGeolocation')
        watchId = await watchPosition((coords, error) => {
          if (cancelled || !coords) {
            if (error) console.warn('AGC: GPS watch error:', error)
            return
          }
          const raw: [number, number] = [coords.latitude, coords.longitude]

          // AGC: Moving average filter to smooth GPS noise
          posBufferRef.current.push(raw)
          if (posBufferRef.current.length > GPS_BUFFER_SIZE) {
            posBufferRef.current.shift()
          }
          const buf = posBufferRef.current
          const smoothed: [number, number] = [
            buf.reduce((s, p) => s + p[0], 0) / buf.length,
            buf.reduce((s, p) => s + p[1], 0) / buf.length,
          ]

          setCurrentPosition(smoothed)
          setPathPoints(prev => [...prev, smoothed])
        }, { enableHighAccuracy: true, timeout: 10000 })
      } catch {
        console.warn('AGC: watchPosition not available')
      }
    }

    startWatch()

    return () => {
      cancelled = true
      if (watchId != null) {
        import('../../services/nativeGeolocation').then(({ clearWatch }) => {
          clearWatch(watchId!)
        }).catch(() => {})
      }
    }
  }, [isRecording, isPaused])

  // AGC: Start recording
  const handleStart = useCallback(() => {
    setIsRecording(true)
    setIsPaused(false)
    
    // AGC: Add starting point
    if (pathPoints.length === 0) {
      setPathPoints([currentPosition])
    }
  }, [currentPosition, pathPoints.length])

  // AGC: Pause recording
  const handlePause = useCallback(() => {
    setIsPaused(true)
  }, [])

  // AGC: Resume recording
  const handleResume = useCallback(() => {
    setIsPaused(false)
  }, [])

  // AGC: Stop recording and show preview
  const handleStop = useCallback(() => {
    if (pathPoints.length < 3) {
      alert('É necessário pelo menos 3 pontos para criar um lote. Continue gravando.')
      return
    }

    setIsRecording(false)
    setIsPaused(false)

    // AGC: Close the polygon and calculate
    const closedPath = [...pathPoints, pathPoints[0]]
    const area = calculatePolygonArea(pathPoints)
    const perimeter = calculatePolygonPerimeter(pathPoints)

    setCalculatedData({ area, perimeter })
    setShowPreview(true)
  }, [pathPoints])

  // AGC: Save lot
  const handleSave = useCallback(() => {
    if (calculatedData) {
      onSave({
        name: lotName,
        coordinates: pathPoints,
        area: calculatedData.area,
        perimeter: calculatedData.perimeter,
      })
    }
  }, [calculatedData, lotName, pathPoints, onSave])

  // AGC: Back to editing
  const handleBackToEdit = useCallback(() => {
    setShowPreview(false)
    setCalculatedData(null)
  }, [])

  return (
    <div className="fixed inset-0 z-[90] bg-gray-900">
      {/* AGC: Top Bar - Prominent green gradient header matching AI One-Click style */}
      <div className="absolute top-0 left-0 right-0 z-[1000] bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 px-4 pt-4 pb-3 shadow-lg">
        <div className="flex items-center justify-between mb-1">
          <button
            onClick={onCancel}
            className="flex items-center gap-2 text-white"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
        </div>
        <h1 className="text-white text-xl font-bold">Criar lote com GPS</h1>
        {!isRecording && !showPreview && (
          <p className="text-white/80 text-sm mt-1">
            Toque em &quot;Minha localização&quot; para ir até você, depois &quot;Iniciar&quot; e caminhe ao redor do lote
          </p>
        )}
        {isRecording && !isPaused && (
          <p className="text-white/80 text-sm mt-1 animate-pulse">
            🔴 Gravando caminho... caminhe ao redor do lote
          </p>
        )}
        {isPaused && (
          <p className="text-white/80 text-sm mt-1">
            ⏸️ Pausado — retome ou conclua o traçado
          </p>
        )}
      </div>

      {/* AGC: Map */}
      <MapContainer
        center={currentPosition}
        zoom={18}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <MapController onMapReady={(m) => { mapRef.current = m }} />
        {/* AGC: Satellite/Terrain view using ESRI World Imagery */}
        <TileLayer
          attribution='Tiles &copy; Esri'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />

        {/* AGC: Auto-center on current position */}
        <AutoCenter center={currentPosition} />

        {/* AGC: Current position marker */}
        <Marker position={currentPosition} />

        {/* AGC: Path line */}
        {pathPoints.length >= 2 && (
          <Polyline
            positions={pathPoints}
            pathOptions={{
              color: '#3B82F6',
              weight: 4,
              opacity: 0.8,
            }}
          />
        )}

        {/* AGC: Closed polygon (when stopped) */}
        {showPreview && pathPoints.length >= 3 && (
          <Polyline
            positions={[...pathPoints, pathPoints[0]]}
            pathOptions={{
              color: '#F59E0B',
              fillColor: '#FCD34D',
              fillOpacity: 0.3,
              weight: 3,
            }}
          />
        )}
      </MapContainer>

      {/* AGC: My Location button - below header, top right */}
      <button
        onClick={handleGoToMyLocation}
        disabled={isGettingLocation || (isRecording && !isPaused)}
        className="absolute right-4 z-[1000] w-11 h-11 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ top: isRecording || showPreview ? 100 : 120 }}
        title="Minha localização"
      >
        {isGettingLocation ? (
          <span className="animate-spin text-lg">⏳</span>
        ) : (
          <Locate className="w-5 h-5 text-[#0B5F4A]" />
        )}
      </button>

      {/* AGC: Status Info (below header, center) */}
      <div className="absolute left-1/2 -translate-x-1/2 z-[1000] bg-white/90 backdrop-blur rounded-lg shadow-lg px-4 py-2" style={{ top: isRecording || showPreview ? 100 : 120 }}>
        <div className="flex items-center gap-2">
          <Navigation className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-900">
            {isRecording && !isPaused ? '🔴 Gravando' : isPaused ? '⏸️ Pausado' : '⏹️ Parado'}
          </span>
          <span className="text-xs text-gray-600 ml-2">
            {pathPoints.length} pontos
          </span>
        </div>
      </div>

      {/* AGC: Preview Panel (after stop) */}
      {showPreview && calculatedData && (
        <div className="absolute bottom-20 left-4 right-4 z-[1000] bg-white rounded-lg shadow-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{lotName}</h2>
          
          <div className="space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-gray-600">Área:</span>
              <span className="font-semibold text-gray-900">{formatArea(calculatedData.area)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Perímetro:</span>
              <span className="font-semibold text-gray-900">{formatPerimeter(calculatedData.perimeter)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Pontos GPS:</span>
              <span className="font-semibold text-gray-900">{pathPoints.length}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleBackToEdit}
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
            >
              Voltar
            </button>
            <button
              onClick={handleSave}
              className="flex-1 px-4 py-3 bg-[#0B5F4A] rounded-lg text-white font-medium hover:bg-[#094935]"
            >
              Salvar lote
            </button>
          </div>
        </div>
      )}

      {/* AGC: Control Buttons - Raised to avoid Leaflet zoom control overlap (bottom-right) */}
      {!showPreview && (
        <div className="absolute left-4 right-4 z-[1000]" style={{ bottom: 96 }}>
          <div className="flex gap-2">
            {!isRecording ? (
              // AGC: Start button
              <button
                onClick={handleStart}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 rounded-lg text-white text-sm font-medium shadow-lg"
              >
                <Play className="w-4 h-4" />
                Iniciar
              </button>
            ) : (
              <>
                {/* AGC: Pause/Resume button */}
                <button
                  onClick={isPaused ? handleResume : handlePause}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 bg-yellow-500 rounded-lg text-white text-sm font-medium shadow-lg"
                >
                  <Pause className="w-4 h-4" />
                  {isPaused ? 'Retomar' : 'Pausar'}
                </button>

                {/* AGC: Stop button */}
                <button
                  onClick={handleStop}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-3 bg-[#0B5F4A] rounded-lg text-white text-sm font-medium shadow-lg"
                >
                  <StopCircle className="w-4 h-4" />
                  Concluir
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* AGC: Blocked permission modal -- shown when user has permanently denied location */}
      <PermissionBlockedModal
        isOpen={showPermBlocked}
        permissionType="location"
        onClose={() => setShowPermBlocked(false)}
      />
    </div>
  )
}

export default CreateWithGPS

